import { useState, useEffect, useCallback } from 'react';
  const perms = detectPerms();
import { 
  Plus, Pencil, Trash2, X, Package, Search, AlertCircle, Loader2, Upload, Image
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  name: '',
  product_code: '',
  hsn_code: '',
  category_id: '',
  sub_category_id: '',
  child_sub_category_id: '',
  specification_id: '',
  sub_specification_id: '',
  unit_id: '',
  opening_stock: '',
  images: [],
  active: true,
  is_part_sale: false,
  part_sale_qty: '',
  part_sale_unit_id: ''
};

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Master data for dropdowns
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);

  // All dropdown options (fetched once, independent of each other)
  const [subCategories, setSubCategories] = useState([]);
  const [childSubCategories, setChildSubCategories] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [subSpecifications, setSubSpecifications] = useState([]);

  // Name parts for auto-population
  const [nameParts, setNameParts] = useState({
    category: '', subCategory: '', childSubCategory: '', specification: '', subSpecification: ''
  });

  // Duplicate name warning
  const [duplicateName, setDuplicateName] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const [productsRes, catsRes, unitsRes, sc1Res, sc2Res, sc3Res, sc4Res] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/categories`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/sub-categories?level=1`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/sub-categories?level=2`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/sub-categories?level=3`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/sub-categories?level=4`, getAuthHeaders()),
      ]);
      setProducts(productsRes.data);
      setCategories(catsRes.data);
      setUnits(unitsRes.data);
      setSubCategories(sc1Res.data);
      setChildSubCategories(sc2Res.data);
      setSpecifications(sc3Res.data);
      setSubSpecifications(sc4Res.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Check for duplicate product name
  const checkDuplicateName = useCallback((name) => {
    if (!name.trim()) { setDuplicateName(false); return; }
    const exists = products.some(p =>
      p.name.toLowerCase() === name.trim().toLowerCase() &&
      (!editingProduct || p.id !== editingProduct.id)
    );
    setDuplicateName(exists);
  }, [products, editingProduct]);

  // Build auto-populated product name from name parts
  const buildProductName = useCallback((parts) => {
    const segments = [parts.category, parts.subCategory, parts.childSubCategory, parts.specification, parts.subSpecification]
      .filter(Boolean);
    return segments.join(' / ');
  }, []);

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    const newParts = { ...nameParts, category: cat?.name || '' };
    setNameParts(newParts);
    const newName = buildProductName(newParts);
    setFormData(prev => ({ ...prev, category_id: categoryId, name: newName }));
    checkDuplicateName(newName);
  };

  // Handle sub category change
  const handleSubCategoryChange = (subCatId) => {
    const sc = subCategories.find(s => s.id === subCatId);
    const newParts = { ...nameParts, subCategory: sc?.name || '' };
    setNameParts(newParts);
    const newName = buildProductName(newParts);
    setFormData(prev => ({ ...prev, sub_category_id: subCatId, name: newName }));
    checkDuplicateName(newName);
  };

  // Handle child sub category change
  const handleChildSubCategoryChange = (childId) => {
    const csc = childSubCategories.find(c => c.id === childId);
    const newParts = { ...nameParts, childSubCategory: csc?.name || '' };
    setNameParts(newParts);
    const newName = buildProductName(newParts);
    setFormData(prev => ({ ...prev, child_sub_category_id: childId, name: newName }));
    checkDuplicateName(newName);
  };

  // Handle specification change
  const handleSpecificationChange = (specId) => {
    const sp = specifications.find(s => s.id === specId);
    const newParts = { ...nameParts, specification: sp?.name || '' };
    setNameParts(newParts);
    const newName = buildProductName(newParts);
    setFormData(prev => ({ ...prev, specification_id: specId, name: newName }));
    checkDuplicateName(newName);
  };

  // Handle sub specification change
  const handleSubSpecificationChange = (subSpecId) => {
    const ss = subSpecifications.find(s => s.id === subSpecId);
    const newParts = { ...nameParts, subSpecification: ss?.name || '' };
    setNameParts(newParts);
    const newName = buildProductName(newParts);
    setFormData(prev => ({ ...prev, sub_specification_id: subSpecId, name: newName }));
    checkDuplicateName(newName);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newVal }));
    if (name === 'name') checkDuplicateName(value);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 2 * 1024 * 1024) { setError('Each image must be less than 2MB'); return; }
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) { setError('Images must be PNG or JPG'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, images: [...prev.images, reader.result] }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setEditingProduct(null);
    setError('');
    setDuplicateName(false);
    setNameParts({ category: '', subCategory: '', childSubCategory: '', specification: '', subSpecification: '' });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setFormData({
      name: product.name || '',
      product_code: product.product_code || '',
      hsn_code: product.hsn_code || '',
      category_id: product.category_id || '',
      sub_category_id: product.sub_category_id || '',
      child_sub_category_id: product.child_sub_category_id || '',
      specification_id: product.specification_id || '',
      sub_specification_id: product.sub_specification_id || '',
      unit_id: product.unit_id || '',
      opening_stock: product.opening_stock ?? '',
      images: product.images || [],
      active: product.active ?? true,
      is_part_sale: product.is_part_sale ?? false,
      part_sale_qty: product.part_sale_qty ?? '',
      part_sale_unit_id: product.part_sale_unit_id || ''
    });
    setNameParts({
      category: product.category_name || '',
      subCategory: product.sub_category_name || '',
      childSubCategory: product.child_sub_category_name || '',
      specification: product.specification_name || '',
      subSpecification: product.sub_specification_name || ''
    });
    setEditingProduct(product);
    setError('');
    setDuplicateName(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) { setError('Product name is required'); return; }
    if (duplicateName) { setError('A product with this name already exists'); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        opening_stock: formData.opening_stock !== '' ? Number(formData.opening_stock) : 0,
        part_sale_qty: formData.is_part_sale && formData.part_sale_qty !== '' ? Number(formData.part_sale_qty) : null,
      };
      // Clean empty string IDs to null
      ['category_id', 'sub_category_id', 'child_sub_category_id', 'specification_id', 'sub_specification_id', 'unit_id', 'part_sale_unit_id'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });

      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/products/${editingProduct.id}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/products`, payload, getAuthHeaders());
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/products/${productId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete product');
    }
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.product_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="products-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Products</h1>
          <p className="text-slate-600 mt-1">Manage your product catalog</p>
        </div>
        <button onClick={openAddModal} data-testid="add-product-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Add Product
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search products..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {/* Error */}
      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Products Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-sm border border-slate-200">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 text-center py-16" data-testid="empty-state">
          <Package className="h-16 w-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No products yet</h3>
          <p className="text-slate-500 mb-6">Start by adding your first product</p>
          <button onClick={openAddModal} data-testid="empty-add-btn"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
            <Plus className="h-5 w-5" /> Add First Product
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Product Name</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Code</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Sub Category</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Unit</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id} data-testid={`product-row-${product.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {product.images?.length > 0 ? (
                        <img src={product.images[0]} alt="" className="w-10 h-10 object-cover rounded border border-slate-200" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center border border-slate-200">
                          <Package className="h-5 w-5 text-slate-300" />
                        </div>
                      )}
                      <span className="font-medium text-slate-900">{product.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 font-mono">{product.product_code || '-'}</td>
                  <td className="px-6 py-4 text-sm">
                    {product.category_name ? (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">{product.category_name}</span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {product.sub_category_name ? (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">{product.sub_category_name}</span>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{product.unit_name || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${product.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {product.active ? 'Active' : 'Inactive'}
                    </span>
                    {product.is_part_sale && (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700 ml-1">
                        Part Sale
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {perms.canEdit && (
                      <button onClick={() => openEditModal(product)} data-testid={`edit-btn-${product.id}`}
                        className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                        <Pencil className="h-4 w-4" />
                      </button>
                      )}
                      {perms.canDelete && (
                      <button onClick={() => setDeleteConfirm(product.id)} data-testid={`delete-btn-${product.id}`}
                        className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this product? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-5xl my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button onClick={() => setShowModal(false)} data-testid="modal-close-btn"
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 max-h-[80vh] overflow-y-auto">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}

              {/* Section 1: Classification (Cascading Dropdowns) */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 pb-2 border-b border-slate-200">
                  Classification
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                    <select value={formData.category_id} onChange={(e) => handleCategoryChange(e.target.value)}
                      data-testid="input-category"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                      <option value="">Select Category</option>
                      {categories.filter(c => c.active).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sub Category */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Sub Category</label>
                    <select value={formData.sub_category_id} onChange={(e) => handleSubCategoryChange(e.target.value)}
                      data-testid="input-sub-category"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                      <option value="">Select Sub Category</option>
                      {subCategories.filter(s => s.active).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Child Sub Category */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Child Sub Category</label>
                    <select value={formData.child_sub_category_id} onChange={(e) => handleChildSubCategoryChange(e.target.value)}
                      data-testid="input-child-sub-category"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                      <option value="">Select Child Sub Category</option>
                      {childSubCategories.filter(c => c.active).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Specification */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Specification</label>
                    <select value={formData.specification_id} onChange={(e) => handleSpecificationChange(e.target.value)}
                      data-testid="input-specification"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                      <option value="">Select Specification</option>
                      {specifications.filter(s => s.active).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sub Specification */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Sub Specification</label>
                    <select value={formData.sub_specification_id} onChange={(e) => handleSubSpecificationChange(e.target.value)}
                      data-testid="input-sub-specification"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                      <option value="">Select Sub Specification</option>
                      {subSpecifications.filter(s => s.active).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Product Name (Auto-populated) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                      data-testid="input-name" placeholder="Auto-populated from selections"
                      className={`w-full px-3 py-2 border rounded-sm focus:outline-none font-medium ${duplicateName ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-slate-200 bg-orange-50 focus:border-orange-500'}`} />
                    {duplicateName ? (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> A product with this name already exists
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">Auto-fills from dropdowns. You can edit manually.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Section 2: Product Details */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 pb-2 border-b border-slate-200">
                  Product Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Product Code</label>
                    <input type="text" name="product_code" value={formData.product_code} onChange={handleInputChange}
                      data-testid="input-code" placeholder="e.g., PRD-001"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">HSN Code</label>
                    <input type="text" name="hsn_code" value={formData.hsn_code} onChange={handleInputChange}
                      data-testid="input-hsn" placeholder="Optional"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Unit of Measurement</label>
                    <select name="unit_id" value={formData.unit_id} onChange={handleInputChange}
                      data-testid="input-unit"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                      <option value="">Select Unit</option>
                      {units.filter(u => u.active).map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Opening Stock</label>
                    <input type="number" name="opening_stock" value={formData.opening_stock} onChange={handleInputChange}
                      data-testid="input-opening-stock" placeholder="0" step="1" min="0"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" name="active" checked={formData.active} onChange={handleInputChange}
                        data-testid="input-active"
                        className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500" />
                      <span className="text-sm font-medium text-slate-700">Active</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Section 3: Part Sale Product */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 pb-2 border-b border-slate-200">
                  Part Sale
                </h3>
                <div className="mb-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="is_part_sale" checked={formData.is_part_sale} onChange={handleInputChange}
                      data-testid="input-part-sale"
                      className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500" />
                    <span className="text-sm font-medium text-slate-700">This is a Part Sale Product</span>
                  </label>
                  <p className="text-xs text-slate-400 mt-1 ml-8">Enable if this product is sold in parts (e.g., wire sold by meters from a box)</p>
                </div>
                {formData.is_part_sale && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-orange-50 border border-orange-200 rounded-sm p-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Full Quantity <span className="text-red-500">*</span></label>
                      <input type="number" name="part_sale_qty" value={formData.part_sale_qty} onChange={handleInputChange}
                        data-testid="input-part-sale-qty" placeholder="e.g., 100" step="0.01" min="0"
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Unit <span className="text-red-500">*</span></label>
                      <select name="part_sale_unit_id" value={formData.part_sale_unit_id} onChange={handleInputChange}
                        data-testid="input-part-sale-unit"
                        className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                        <option value="">Select Unit</option>
                        {units.filter(u => u.active).map(u => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 4: Product Images */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 pb-2 border-b border-slate-200">
                  Product Images
                </h3>
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors mb-4">
                  <Upload className="h-4 w-4" /><span>Add Images</span>
                  <input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleImageUpload}
                    multiple className="hidden" data-testid="input-images" />
                </label>

                {formData.images.length > 0 ? (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img src={image} alt={`Product ${index + 1}`}
                          className="w-full h-24 object-cover border border-slate-200 rounded-sm" />
                        <button type="button" onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-24 border-2 border-dashed border-slate-200 rounded-sm">
                    <div className="text-center text-slate-400">
                      <Image className="h-6 w-6 mx-auto mb-1" />
                      <p className="text-xs">No images uploaded</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={submitting || duplicateName} data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
